import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { SERVICES, WHATSAPP_URL } from "../lib/content";

export default function Servicos() {
  return (
    <>
      {/* Hero */}
      <section className="pt-28 md:pt-36 pb-12 md:pb-16 bg-white" data-testid="servicos-index-hero">
        <div className="sca-container">
          <Reveal>
            <p className="sca-overline mb-6">[ Serviços ]</p>
            <h1 className="sca-h1 max-w-[20ch]">
              Cada segmento tratado como um <span className="sca-serif text-sca-red">sistema de engenharia próprio</span>.
            </h1>
            <p className="sca-body mt-8 max-w-2xl">
              Conheça abaixo as áreas em que a SCA Construtora atua, com detalhamento técnico
              completo de cada serviço — atuação prática e benefícios entregues.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Quick service grid (navigation) */}
      <section className="pb-16 md:pb-20 bg-white">
        <div className="sca-container">
          <Stagger className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-sca-line border-t border-l border-sca-line">
            {SERVICES.map((s) => (
              <StaggerItem key={s.slug}>
                <a
                  href={`#${s.slug}`}
                  data-testid={`servicos-anchor-${s.slug}`}
                  className="group block bg-white p-5 md:p-6 border-r border-b border-sca-line hover:bg-sca-black hover:text-white transition-colors duration-500 h-full"
                >
                  <span className="font-mono text-xs tracking-[0.28em] text-sca-red font-bold">{s.number}</span>
                  <h3 className="font-sans text-sm md:text-base font-semibold leading-tight mt-3 group-hover:text-white">
                    {s.title}
                  </h3>
                </a>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* Full detailed sections for every service */}
      {SERVICES.map((s, idx) => {
        const isAlt = idx % 2 === 1;
        return (
          <section
            key={s.slug}
            id={s.slug}
            data-testid={`servicos-section-${s.slug}`}
            className={`sca-section scroll-mt-32 ${isAlt ? "bg-sca-mist" : "bg-white"}`}
          >
            <div className="sca-container">
              {/* Header row */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-16 items-end">
                <Reveal className="lg:col-span-7">
                  <p className="font-mono text-sm tracking-[0.32em] text-sca-red font-bold mb-5">
                    / {s.number} — Serviço
                  </p>
                  <h2 className="sca-h2 max-w-[22ch]">{s.h1}</h2>
                </Reveal>
                <Reveal delay={0.15} className="lg:col-span-5">
                  <p className="sca-body">{s.intro}</p>
                  <Link
                    to={`/servicos/${s.slug}`}
                    data-testid={`servicos-deep-link-${s.slug}`}
                    className="mt-8 inline-flex items-center gap-3 text-[12px] tracking-[0.22em] uppercase font-bold text-sca-red hover:gap-5 transition-all"
                  >
                    Página dedicada do serviço <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </Reveal>
              </div>

              {/* Image */}
              <Reveal>
                <div className="aspect-[21/9] overflow-hidden bg-sca-mist mb-16">
                  <img
                    src={s.image}
                    alt={s.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </Reveal>

              {/* Two-column sections — Atuação + Benefícios */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
                {s.sections.map((sec, sIdx) => (
                  <Reveal
                    key={sIdx}
                    delay={sIdx * 0.1}
                    className={sIdx === 0 ? "lg:col-span-7" : "lg:col-span-5"}
                  >
                    <p className="sca-overline mb-5">
                      [ {sIdx === 0 ? "Atuação" : "Benefícios"} ]
                    </p>
                    <h3 className="sca-h3 mb-8">{sec.title}</h3>
                    <ul
                      className="space-y-0"
                      data-testid={`servicos-${s.slug}-list-${sIdx === 0 ? "atuacao" : "beneficios"}`}
                    >
                      {sec.items.map((it) => (
                        <li
                          key={it}
                          className="flex items-start gap-4 py-3.5 border-b border-sca-line"
                        >
                          <Check className="w-5 h-5 text-sca-red flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                          <span className="font-sans text-base md:text-lg text-sca-black font-medium">
                            {it}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </Reveal>
                ))}
              </div>

              {/* CTA row */}
              <div className="mt-14 pt-10 border-t border-sca-line flex flex-wrap items-center justify-between gap-6">
                <p className="font-sans text-lg md:text-xl text-sca-black max-w-2xl">
                  Pronto para estruturar a sua{" "}
                  <span className="sca-serif text-sca-red">{s.title.toLowerCase()}</span>?
                </p>
                <div className="flex flex-wrap gap-3">
                  <a
                    href={WHATSAPP_URL(`Olá, gostaria de ${s.cta.toLowerCase()} sobre ${s.title}.`)}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`servicos-cta-${s.slug}`}
                    className="btn-sca-primary"
                  >
                    {s.cta} <ArrowUpRight className="w-4 h-4" />
                  </a>
                  <Link
                    to="/contato"
                    className="btn-sca-ghost"
                  >
                    Formulário
                  </Link>
                </div>
              </div>
            </div>
          </section>
        );
      })}
    </>
  );
}
